import React from 'react';
import { ServiceCategory } from '@prisma/client';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import SmallModal from '../../global/SmallModal';
import IOSSwitch from '../../global/IOSSwitch';
import Required from '../../form/base/Required';
import useGetServiceIntakeQuestions from '../../../hooks/businesses/useGetServiceIntakeQuestions';
import useGetDefaultServiceIntakeQuestions from '../../../hooks/businesses/useGetDefaultServiceIntakeQuestions';
import { BusinessService } from '../../../types/businesses';
import useUpdateServiceIntakeQuestions from '../../../hooks/businesses/useUpdateServiceIntakeQuestions';
import { getServiceDescriptionLabel } from '../../../helpers/services';

interface Props {
  open: boolean;
  service: BusinessService | null;
  serviceCategory?: ServiceCategory;
  pendingQuestions?: { questionId: string; enabled: boolean }[];
  onClose: () => void;
  onSaved: () => void;
  onLocalSave?: (questions: { questionId: string; enabled: boolean }[]) => void;
}

function CustomizeBookingFormModal({
  open,
  serviceCategory,
  service,
  pendingQuestions,
  onClose,
  onSaved,
  onLocalSave,
}: Props) {
  const { questions, isLoading, refetch } = useGetServiceIntakeQuestions(
    service?.serviceId ?? null,
  );

  const { mutate: updateQuestions, isPending: isSaving } =
    useUpdateServiceIntakeQuestions({
      onSuccess: async () => {
        await refetch();
        toast.success('Booking form updated!');
        onSaved();
        onClose();
      },
      onError: () => {
        toast.error('Failed to update booking form.');
      },
    });

  const buildEnabled = React.useCallback(
    (qs: typeof questions) => {
      const pending = Object.fromEntries(
        (pendingQuestions ?? []).map(q => [q.questionId, q.enabled]),
      );
      return Object.fromEntries(
        qs.map(q => [q.questionId, pending[q.questionId] ?? q.enabled]),
      );
    },
    [pendingQuestions],
  );

  const [enabled, setEnabled] = React.useState<Record<string, boolean>>(() =>
    buildEnabled(questions),
  );

  React.useEffect(() => {
    if (!questions.length) return;
    setEnabled(buildEnabled(questions));
  }, [questions, buildEnabled]);

  const handleToggle = (questionId: string) => {
    setEnabled(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const { defaultQuestions } = useGetDefaultServiceIntakeQuestions();

  const handleReset = () => {
    const reset: Record<string, boolean> = {};
    defaultQuestions.forEach(q => {
      reset[q.questionId] = q.enabled;
    });
    setEnabled(reset);
  };

  const handleSave = () => {
    const payload = questions
      .filter(q => !q.requiredForServices)
      .map(q => ({
        questionId: q.questionId,
        enabled: !!enabled[q.questionId] || q.requiredForServices,
      }));

    if (onLocalSave) {
      onLocalSave(payload);
      onClose();
      return;
    }

    if (!service) return;
    updateQuestions({ serviceId: service.serviceId, questions: payload });
  };

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      title="Customize Booking Form"
      footer={{
        button: {
          text: 'Save',
          onClick: handleSave,
          loading: isSaving,
          disabled: isSaving || isLoading,
          type: 'button',
        },
      }}>
      <Stack p={2} gap={0} width="100%" boxSizing="border-box">
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Toggle questions on or off to choose what to include on your
              booking form.
            </Typography>
            <Stack>
              {questions.map(q => {
                const description = q.description || '';
                if (
                  serviceCategory &&
                  serviceCategory !== 'PIERCING' &&
                  q.question === 'Location'
                )
                  return null;
                return (
                  <Stack
                    key={q.questionId}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    py={1.5}>
                    <Box flexShrink={1}>
                      <Typography variant="h5">
                        {q.question}
                        {q.requiredForServices && <Required />}
                      </Typography>
                      {description && (
                        <Typography variant="body2" color="text.secondary">
                          {description.replaceAll(
                            '{{service}}',
                            getServiceDescriptionLabel(serviceCategory),
                          )}
                        </Typography>
                      )}
                    </Box>
                    <IOSSwitch
                      checked={!!enabled[q.questionId]}
                      onChange={() => handleToggle(q.questionId)}
                      disabled={q.requiredForServices}
                    />
                  </Stack>
                );
              })}
            </Stack>
            <Button
              variant="light"
              color="info"
              fullWidth
              onClick={handleReset}
              sx={{ alignSelf: 'center' }}>
              Reset to Default
            </Button>
          </>
        )}
      </Stack>
    </SmallModal>
  );
}

export default CustomizeBookingFormModal;
